/**
 * Shared formatting utilities for TLS UI atoms.
 *
 * §01.9 (Foundations, "Formatting · canonical examples"): duration is m:ss,
 * and ss.SS when precision matters — "0:02 in a row. 0:00.62 / 0:02.00 in
 * the player. Never '2s', never '2 seconds' outside prose."
 */

/**
 * Row/meta form: "0:02". Whole seconds, floored — precision belongs to
 * fmtDurationPrecise. One exception: a sub-second sound (0 < d < 1s) rounds
 * UP to "0:01" so a real clip never reads as "0:00" in a row (F-9). Only a
 * true zero / missing duration renders "0:00".
 */
export function fmtDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  const whole = seconds < 1 ? 1 : Math.floor(seconds);
  const m = Math.floor(whole / 60);
  const sec = whole % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

/**
 * Precision form for players, trimmers and compatibility checks: "0:00.62",
 * "0:02.00" — two fixed decimals so a value near the 3-second Tesla limit
 * reads exactly, per §01.9's "ss.SS when precision matters".
 */
export function fmtDurationPrecise(seconds: number): string {
  let m = Math.floor(seconds / 60);
  let s = (seconds - m * 60).toFixed(2);
  // toFixed can round 59.999 up to "60.00" — carry into the minute.
  if (s === "60.00") {
    m += 1;
    s = "0.00";
  }
  return `${m}:${s.padStart(5, "0")}`;
}

/**
 * File size, one format everywhere (DS §01.9): whole KB below 1000 KB, then
 * one-decimal MB, always with a space before the unit ("342 KB", "1.2 MB").
 * Never bytes.
 *
 * Accepts bytes (number) or the free-form strings the catalog stores
 * ("334.6 KB", "342KB", "1.2 MB", "245000"). Returns "" when the input has
 * no parsable number so callers can fall back to their own placeholder.
 */
export function fmtFileSize(input: number | string | null | undefined): string {
  if (input === null || input === undefined) return "";
  let bytes: number;
  if (typeof input === "number") {
    bytes = input;
  } else {
    const m = input.trim().match(/^([\d.,]+)\s*(B|KB|MB|GB)?$/i);
    if (!m) return "";
    const value = Number.parseFloat(m[1].replace(/,/g, ""));
    if (!Number.isFinite(value)) return "";
    const unit = (m[2] ?? "B").toUpperCase();
    const mult: Record<string, number> = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3 };
    bytes = value * mult[unit];
  }
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  const kb = bytes / 1024;
  if (kb < 1000) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
